import { Button } from '@openx/components-core/lib/Button/Button';
import {
  type AppBundleIdOptionPayload,
  type AppInventoryOptionPayload,
  type AppInventoryType,
  type InstanceProps,
  type IntersectOptionPayload,
  type InventoryAndContentOptionPayload,
  type InventoryAndContentState,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import type { AnyAction } from '@reduxjs/toolkit';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomOptionsContext } from '../../../../utils/hooks/useCustomOptionsContext';
import type { InvalidItemsError } from '../../../shared/BulkAdd/types';
import { INVENTORY_LIMITS } from '../../../shared/InventoryAndContent/constants';
import { setComparisonOpValue } from '../../../shared/InventoryAndContent/inventoryHelpers';
import { KeywordsInput } from '../../../shared/KeywordsInput/KeywordsInput';
import type { SetAppInventoryTypeDispatch, SetComparisonTypeDispatch } from '../constants';
import { AdUnitSize } from '../partials/AdUnitSize';
import { AppInventoryComparison } from '../partials/AppInventoryComparison';
import { Comparison } from '../partials/Comparison';
import { Inventory } from '../partials/Inventory';
import { InventoryIDs } from '../partials/InventoryIDs';
import { type DrawerComponentsMap, filterTypes } from '../utils/filterTypes';
import { getItemsCount } from '../utils/getItemsCount';

export type UseGetDrawerContentProps = {
  targetingParams: InventoryAndContentState;
  contentOption: InventoryContentOption;
  comparisonType: ComparisonType;
  inputInstanceValue: InstanceProps | null;
  isBefInstance: boolean;
  dispatchInventory: (action: AnyAction) => void;
  setComparisonType: SetComparisonTypeDispatch;
  dispatchInstance: (action: AnyAction) => void;
  setInputInstanceValue: Dispatch<SetStateAction<InstanceProps | null>>;
  onBulkOpen: () => void;
  // App Inventory specific props
  appInventoryType?: AppInventoryType;
  setAppInventoryType?: SetAppInventoryTypeDispatch;
  appNameComparisonType?: ComparisonType;
  bundleIdComparisonType?: ComparisonType;
  accountUid?: string;
  setAppBundleIdDuplicateErrors?: Dispatch<SetStateAction<InvalidItemsError | null>>;
};

export const useGetDrawerContent = ({
  dispatchInventory,
  targetingParams,
  contentOption,
  comparisonType,
  setComparisonType,
  dispatchInstance,
  inputInstanceValue,
  setInputInstanceValue,
  isBefInstance,
  onBulkOpen,
  // App Inventory specific props
  appInventoryType: appInventoryTypeProp,
  setAppInventoryType: setAppInventoryTypeProp,
  appNameComparisonType: appNameComparisonTypeProp,
  bundleIdComparisonType: bundleIdComparisonTypeProp,
  accountUid,
  setAppBundleIdDuplicateErrors,
}: UseGetDrawerContentProps) => {
  const customOptions = useCustomOptionsContext<InventoryContentOption>();
  const inventoryContent = targetingParams[contentOption] as InventoryAndContentOptionPayload | IntersectOptionPayload;
  const { t } = useTranslation();

  const drawerContent: Partial<DrawerComponentsMap> = useMemo(() => {
    const allTypes: DrawerComponentsMap = {
      [InventoryContentOption.ADUNIT]: (
        <InventoryIDs
          inventoryContent={inventoryContent as IntersectOptionPayload}
          dispatchInventory={dispatchInventory}
          selectedType={InventoryContentOption.ADUNIT}
        />
      ),
      [InventoryContentOption.ADUNIT_SIZE]: (
        <AdUnitSize targetingParams={targetingParams} dispatchInventory={dispatchInventory} />
      ),
      [InventoryContentOption.APP_BUNDLE_ID]: (
        <Comparison
          contentOption={contentOption}
          comparisonType={comparisonType}
          dispatchInventory={dispatchInventory}
          inventoryContent={inventoryContent}
          itemsCount={getItemsCount(inventoryContent?.val)}
          setComparisonType={setComparisonType}
        />
      ),
      [InventoryContentOption.INVENTORY]: (
        <Inventory
          targetingParams={targetingParams}
          dispatchInventory={dispatchInventory}
          dispatchInstance={dispatchInstance}
          inputInstanceValue={inputInstanceValue}
          setInputInstanceValue={setInputInstanceValue}
        />
      ),
      [InventoryContentOption.PAGE_URL]: (
        <Comparison
          contentOption={contentOption}
          comparisonType={comparisonType}
          dispatchInventory={dispatchInventory}
          inventoryContent={inventoryContent}
          itemsCount={getItemsCount(inventoryContent?.val)}
          setComparisonType={setComparisonType}
        />
      ),
      [InventoryContentOption.PUBLISHER_ID]: (
        <InventoryIDs
          inventoryContent={inventoryContent as IntersectOptionPayload}
          dispatchInventory={dispatchInventory}
          selectedType={InventoryContentOption.PUBLISHER_ID}
          targetingParams={targetingParams}
        />
      ),
      [InventoryContentOption.INSTANCE_HASH]: (
        <InventoryIDs
          inventoryContent={inventoryContent as IntersectOptionPayload}
          dispatchInventory={dispatchInventory}
          selectedType={InventoryContentOption.INSTANCE_HASH}
        />
      ),
      [InventoryContentOption.SITE]: (
        <InventoryIDs
          inventoryContent={inventoryContent as IntersectOptionPayload}
          dispatchInventory={dispatchInventory}
          selectedType={InventoryContentOption.SITE}
        />
      ),
      [InventoryContentOption.KEYWORDS]: (
        <KeywordsInput
          keywords={targetingParams[InventoryContentOption.KEYWORDS].val}
          onChange={keywords => {
            dispatchInventory({
              payload: Array.from(keywords),
              type: setComparisonOpValue[InventoryContentOption.KEYWORDS],
            } as unknown as AnyAction);
          }}
          keywordsLimit={INVENTORY_LIMITS.keywords}
        />
      ),
      [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: (
        <InventoryIDs
          inventoryContent={inventoryContent as IntersectOptionPayload}
          dispatchInventory={dispatchInventory}
          selectedType={InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID}
        />
      ),
      [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: (
        <Button onClick={onBulkOpen} data-test="create-custom-rule-button" sx={{ mt: 2 }}>
          {t('Create Custom Rule')}
        </Button>
      ),
      // App Inventory uses AppInventoryComparison with type dropdown + input
      // Toggle ALLOW/BLOCK is in the Items section below
      [InventoryContentOption.APP_GROUP]: (
        <AppInventoryComparison
          appInventoryType={appInventoryTypeProp}
          setAppInventoryType={setAppInventoryTypeProp}
          appNameComparisonType={appNameComparisonTypeProp}
          bundleIdComparisonType={bundleIdComparisonTypeProp}
          dispatchInventory={dispatchInventory}
          appInventoryContent={targetingParams[InventoryContentOption.APP_GROUP] as AppInventoryOptionPayload}
          appBundleIdContent={targetingParams[InventoryContentOption.APP_BUNDLE_ID] as AppBundleIdOptionPayload}
          accountUid={accountUid}
          setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
        />
      ),
    };

    return filterTypes(allTypes, isBefInstance, customOptions);
  }, [
    inventoryContent,
    dispatchInventory,
    targetingParams,
    contentOption,
    comparisonType,
    setComparisonType,
    dispatchInstance,
    inputInstanceValue,
    setInputInstanceValue,
    customOptions,
    isBefInstance,
    onBulkOpen,
    t,
    appInventoryTypeProp,
    setAppInventoryTypeProp,
    appNameComparisonTypeProp,
    bundleIdComparisonTypeProp,
    accountUid,
    setAppBundleIdDuplicateErrors,
  ]);

  return { drawerContent };
};
